import { useCallback, useEffect } from 'react';

import type { OnboardingSchema } from '@/features/onboarding';

import { useAppDispatch } from '@/shared/lib/store';

import {
    useCurrentStepIndex,
    useIsProcessing,
    useOnboardingData,
} from '../model/selectors/onboarding.selectors';
import {
    markStepAsCompleted,
    setCurrentStepIndex,
    setIsProcessing,
} from '../model/slices/onboarding.slice';

interface UseOnboardingCompletionProps {
    totalSteps: number;
    onComplete: (onboardingData: OnboardingSchema) => Promise<void> | void;
    slugs: string[];
}

interface UseOnboardingCompletionReturn {
    handleNext: (stepSlug?: string) => void;
    currentStepIndex: number;
    isFirstStep: boolean;
    isLastStep: boolean;
    isProcessing: boolean;
}

export const useOnboarding = (
    props: UseOnboardingCompletionProps,
): UseOnboardingCompletionReturn => {
    const { totalSteps, onComplete, slugs } = props;

    const dispatch = useAppDispatch();
    const currentStepIndex = useCurrentStepIndex();
    const onboardingData = useOnboardingData();
    const isProcessing = useIsProcessing();

    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === totalSteps - 1;

    // Check if all required steps are completed
    const areAllStepsCompleted = useCallback(() => {
        if (!onboardingData) return false;

        return slugs.every((slug) =>
            onboardingData.onboarding_steps_done.includes(slug),
        );
    }, [onboardingData, slugs]);

    // Auto-complete onboarding when conditions are met
    useEffect(() => {
        if (
            isLastStep &&
            areAllStepsCompleted() &&
            !isProcessing &&
            onboardingData
        ) {
            const completeOnboarding = async () => {
                dispatch(setIsProcessing(true));
                try {
                    await onComplete(onboardingData);
                } catch (error) {
                    dispatch(setIsProcessing(false));
                }
            };

            completeOnboarding();
        }
    }, [
        isLastStep,
        areAllStepsCompleted,
        isProcessing,
        onboardingData,
        onComplete,
        dispatch,
    ]);

    const handleNext = useCallback(
        (stepSlug?: string) => {
            // Mark current step as completed if slug is provided
            if (stepSlug) {
                dispatch(markStepAsCompleted(stepSlug));
            }

            // Move to next step if not at the last step
            if (currentStepIndex < totalSteps - 1) {
                dispatch(setCurrentStepIndex(currentStepIndex + 1));
            }
            // If at last step, the useEffect will handle completion
        },
        [currentStepIndex, dispatch, totalSteps],
    );

    return {
        handleNext,
        currentStepIndex,
        isFirstStep,
        isLastStep,
        isProcessing,
    };
};
